import React, { useState, useMemo, useEffect, useContext, useRef, useCallback } from "react";
import Loder from "../../Components/Loder";
import Chatbox from "../../Components/Chatbox";
import { ThemeContext } from "../../Context/ThemeContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { fetchWithAuth } from '../../utils/userapi';
import { UserContext } from "../../Context/UserContext";
import { Link } from "react-router-dom";

import {
    faRepeat,
    faFolderOpen,
    faSearch,
    faSort,
    faSortUp,
    faSortDown,
    faTimes
} from '@fortawesome/free-solid-svg-icons';

// ✅ Create a separate component for the popup with memoization
const RedesignPopup = React.memo(({
    theme,
    showRedesignPopup,
    pendingRedesignOrders,
    redesignMessage,
    setRedesignMessage,
    isSubmitting,
    handleRedesignSubmit,
    setShowRedesignPopup
}) => {
    const textareaRef = useRef(null);
    const [localMessage, setLocalMessage] = useState("");

    // Initialize local state when popup opens
    useEffect(() => {
        if (showRedesignPopup) {
            setLocalMessage(redesignMessage || "");
        }
    }, [showRedesignPopup, redesignMessage]);

    // Auto-focus when popup opens
    useEffect(() => {
        if (showRedesignPopup && textareaRef.current) {
            const timer = setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.focus();
                    textareaRef.current.selectionStart = textareaRef.current.value.length;
                    textareaRef.current.selectionEnd = textareaRef.current.value.length;
                }
            }, 10);
            return () => clearTimeout(timer);
        }
    }, [showRedesignPopup]);

    // Debounced update to parent state
    const updateParentMessage = useCallback(
        debounce((message) => {
            setRedesignMessage(message);
        }, 300),
        [setRedesignMessage]
    );

    const handleMessageChange = useCallback((e) => {
        const value = e.target.value;
        setLocalMessage(value);
        updateParentMessage(value);
    }, [updateParentMessage]);

    const handleClosePopup = useCallback(() => {
        if (!isSubmitting) {
            setShowRedesignPopup(false);
            setRedesignMessage("");
        }
    }, [isSubmitting, setShowRedesignPopup, setRedesignMessage]);

    if (!showRedesignPopup) return null;

    return (
        <div className="fixed inset-0 bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`rounded-xl shadow-2xl w-full max-w-md ${theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>
                {/* Header */}
                <div className={`flex justify-between items-center p-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                    <h2 className="text-lg font-bold">
                        Send for Redesign
                    </h2>
                    <button
                        onClick={handleClosePopup}
                        disabled={isSubmitting}
                        className={`p-1 rounded ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-4">
                    <div className="mb-4">
                        <p className="mb-2">
                            Order{pendingRedesignOrders.length > 1 ? 's' : ''} to send for redesign:
                        </p>
                        <div className="flex flex-wrap gap-1">
                            {pendingRedesignOrders.map((id) => (
                                <span
                                    key={id}
                                    className={`px-2 py-1 rounded text-xs ${theme === 'dark' ? 'bg-gray-700' : 'bg-blue-100'}`}
                                >
                                    Order #{id}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className="mb-4">
                        <label className="block mb-1">
                            Message for Design Team <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            ref={textareaRef}
                            value={localMessage}
                            onChange={handleMessageChange}
                            placeholder="Enter reason for redesign or instructions..."
                            className={`w-full p-3 rounded border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-500'}`}
                            rows="4"
                            disabled={isSubmitting}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className={`p-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} flex justify-end gap-2`}>
                    <button
                        onClick={handleClosePopup}
                        disabled={isSubmitting}
                        className={`px-4 py-2 rounded font-medium ${theme === 'dark' ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'} ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleRedesignSubmit}
                        disabled={isSubmitting || !localMessage.trim()}
                        className={`px-4 py-2 rounded font-medium flex items-center gap-2 ${isSubmitting || !localMessage.trim()
                            ? theme === 'dark' ? 'bg-gray-600 text-gray-400 cursor-not-allowed' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700'
                            }`}
                    >
                        {isSubmitting ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Processing...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faRepeat} />
                                Send{pendingRedesignOrders.length > 1 ? ` (${pendingRedesignOrders.length})` : ''}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
});

// Simple debounce utility function
const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

export default function Datatable({
    columns = [],
    data = [],
    rowsPerPageOptions = [50, 100, 200, 500],
    loading = false,
    error = null
}) {
    const { theme } = useContext(ThemeContext);
    const [status, setStatus] = useState("show");
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [orderid, setOrderid] = useState(null);
    const { user } = useContext(UserContext);

    // ✅ NEW STATES for multi-select & dropdown
    const [selectedRows, setSelectedRows] = useState([]);
    const [fileType, setFileType] = useState("stl");

    // ✅ NEW STATES for redesign popup
    const [showRedesignPopup, setShowRedesignPopup] = useState(false);
    const [redesignMessage, setRedesignMessage] = useState("");
    const [pendingRedesignOrders, setPendingRedesignOrders] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Memoize data to prevent unnecessary re-renders
    const memoizedData = useMemo(() => data || [], [data]);

    // ✅ Control loader based on parent's loading prop
    useEffect(() => {
        if (!loading) {
            setStatus("hide");
        } else {
            setStatus("show");
        }
    }, [loading]);

    // Filter & Sort - optimized with useMemo
    const filteredData = useMemo(() => {
        let filtered = memoizedData;

        if (search) {
            const searchLower = search.toLowerCase();
            filtered = filtered.filter((row) =>
                columns.some((col) => {
                    const value = row[col.accessor];
                    return value != null &&
                        String(value).toLowerCase().includes(searchLower);
                })
            );
        }

        if (sortConfig.key) {
            filtered = [...filtered].sort((a, b) => {
                const aVal = a[sortConfig.key];
                const bVal = b[sortConfig.key];

                if (aVal == null) return 1;
                if (bVal == null) return -1;

                const isNumeric = !isNaN(aVal) && !isNaN(bVal);

                if (isNumeric) {
                    return sortConfig.direction === "asc"
                        ? Number(aVal) - Number(bVal)
                        : Number(bVal) - Number(aVal);
                } else {
                    return sortConfig.direction === "asc"
                        ? String(aVal).localeCompare(String(bVal))
                        : String(bVal).localeCompare(String(aVal));
                }
            });
        }

        return filtered;
    }, [search, memoizedData, columns, sortConfig]);

    const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * rowsPerPage;
        return filteredData.slice(start, start + rowsPerPage);
    }, [currentPage, filteredData, rowsPerPage]);

    // Memoize all callbacks
    const handleSearch = useCallback((e) => {
        setSearch(e.target.value);
        setCurrentPage(1);
    }, []);

    const handlePageChange = useCallback((page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    }, [totalPages]);

    const handleSort = useCallback((key) => {
        setSortConfig(prev => {
            if (prev.key === key && prev.direction === "asc") {
                return { key, direction: "desc" };
            } else if (prev.key === key && prev.direction === "desc") {
                return { key: null, direction: "asc" };
            }
            return { key, direction: "asc" };
        });
    }, []);

    const handleRowsPerPageChange = useCallback((e) => {
        setRowsPerPage(parseInt(e.target.value));
        setCurrentPage(1);
    }, []);

    const getPageNumbers = useCallback((totalPages, currentPage) => {
        const maxButtons = 5;
        const pages = [];

        if (totalPages <= maxButtons) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        let startPage = Math.max(1, currentPage - 2);
        let endPage = Math.min(totalPages, currentPage + 2);

        if (startPage > 1) pages.push(1, "...");
        for (let i = startPage; i <= endPage; i++) pages.push(i);
        if (endPage < totalPages) pages.push("...", totalPages);

        return pages;
    }, []);

    const openPopup = useCallback((id) => {
        setOrderid(id);
        document.getElementById('chatbox').style.display = "block";
    }, []);

    const sendRedesign = useCallback(async (orderIds, message) => {
        try {
            const response = await fetchWithAuth("send-for-redesign", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    orders: orderIds,
                    message: message,
                    labname: user.labname
                }),
            });

            return response;
        } catch (error) {
            return {
                status: "error",
                message: "Server error. Please try again later."
            };
        }
    }, []);

    // ✅ Multi-select logic - memoized
    const toggleSelectRow = useCallback((id) =>
        setSelectedRows((prev) =>
            prev.includes(id)
                ? prev.filter((x) => x !== id)
                : [...prev, id]
        ), []);

    const toggleSelectAll = useCallback(() => {
        const visibleIds = paginatedData.map((r) => r.orderid);
        if (paginatedData.every((r) => selectedRows.includes(r.orderid))) {
            setSelectedRows(selectedRows.filter((id) => !visibleIds.includes(id)));
        } else {
            setSelectedRows([...new Set([...selectedRows, ...visibleIds])]);
        }
    }, [paginatedData, selectedRows]);

    // ✅ Open redesign popup - memoized
    const openRedesignPopup = useCallback(() => {
        if (!selectedRows.length) {
            alert("Please select at least one case to proceed with the redesign request.");
            return;
        }

        // Filter valid orders
        const validOrders = selectedRows.filter(id => {
            const row = memoizedData.find((x) => x.orderid === id);
            return row && row.status !== "New" && row.status !== "Redesign";
        });

        if (validOrders.length === 0) {
            alert("Orders with 'New' status or already in 'Redesign' cannot be sent for redesign.");
            return;
        }

        setPendingRedesignOrders(validOrders);
        setRedesignMessage("");
        setShowRedesignPopup(true);
    }, [selectedRows, memoizedData]);

    // ✅ Submit redesign with message - optimized
    const handleRedesignSubmit = useCallback(async () => {
        if (!redesignMessage.trim()) {
            alert("Please enter a message for the redesign request.");
            return;
        }

        setIsSubmitting(true);

        const newOrderIds = [];
        const redesignIds = [];
        const validOrders = [];

        for (let id of pendingRedesignOrders) {
            const r = memoizedData.find((x) => x.orderid === id);

            if (!r) continue;

            if (r.status === "New") {
                newOrderIds.push(id);
                continue;
            }

            if (r.status === "Redesign") {
                redesignIds.push(id);
                continue;
            }

            validOrders.push(id);
        }

        if (newOrderIds.length === pendingRedesignOrders.length) {
            setIsSubmitting(false);
            const msg = newOrderIds.length === 1
                ? `Order ${newOrderIds[0]} cannot be sent for redesign because it is a new order.`
                : `All selected orders cannot be sent for redesign because they are new orders.`;
            alert(msg);
            return;
        }

        if (validOrders.length === 0) {
            setIsSubmitting(false);
            const msg = redesignIds.length === 1
                ? `Order ${redesignIds[0]} is already in redesign process.`
                : `All selected orders are already in redesign process.`;
            alert(msg);
            return;
        }

        const res = await sendRedesign(validOrders, redesignMessage);

        setIsSubmitting(false);
        setShowRedesignPopup(false);
        setRedesignMessage("");
        setSelectedRows([]);

        if (res.status === "success") {
            let successMsg = res.message || "Orders sent for redesign successfully.";

            if (newOrderIds.length > 0) {
                successMsg += "\n\nNote: " + (newOrderIds.length === 1
                    ? `Order ${newOrderIds[0]} was skipped as it's a new order.`
                    : `Orders ${newOrderIds.join(', ')} were skipped as they are new orders.`);
            }

            if (redesignIds.length > 0) {
                successMsg += "\n\nNote: " + (redesignIds.length === 1
                    ? `Order ${redesignIds[0]} was skipped as it's already in redesign.`
                    : `Orders ${redesignIds.join(', ')} were skipped as they are already in redesign.`);
            }

            alert(successMsg);
            window.location.reload();
        } else {
            alert(res.message || "Failed to send orders for redesign.");
        }
    }, [redesignMessage, pendingRedesignOrders, memoizedData, sendRedesign]);

    const base_url = localStorage.getItem('skydent_user_base_url');

    const handleBulkDownload = useCallback(async () => {
        if (!selectedRows.length) {
            alert("Please select at least one record to proceed with the download.");
            return;
        }

        let missingFiles = [];

        for (const id of selectedRows) {
            const row = memoizedData.find((r) => r.orderid === id);
            if (!row) continue;

            try {
                if (fileType === "stl") {
                    // Step 1: get all STL file paths from backend
                    const res = await fetch(`${base_url}/download-all?orderid=${id}`, {
                        headers: { 'X-Tenant': 'skydent' }
                    });
                    const files = await res.json();

                    if (!Array.isArray(files) || files.length === 0) {
                        missingFiles.push(id);
                        continue;
                    }

                    // Step 2: download each STL file via backend
                    for (const filePath of files) {
                        if (!filePath) continue;

                        const encodedPath = encodeURIComponent(filePath);
                        const finalUrl = `${base_url}/download?path=${encodedPath}`;
                        const filename = filePath.split('/').pop();

                        const link = document.createElement("a");
                        link.href = finalUrl;
                        link.target = "_blank";
                        link.download = filename || "download";
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);

                        await new Promise(r => setTimeout(r, 500)); // optional delay
                    }

                } else if (fileType === "initial" || fileType === "finish") {
                    // Get the correct file path
                    let path = fileType === "initial" ? row.file_path : row.finish_file_path;

                    if (!path || path.trim() === "") {
                        missingFiles.push(id);
                        continue;
                    }

                    const encodedPath = encodeURIComponent(path);
                    const finalUrl = `${base_url}/download?path=${encodedPath}`;
                    const filename = `${fileType}_${id}`;

                    const link = document.createElement("a");
                    link.href = finalUrl;
                    link.target = "_blank";
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            } catch (err) {
                console.error("Download error:", err);
                missingFiles.push(id);
            }
        }

        if (missingFiles.length > 0) {
            alert(`Files not found for order IDs: ${missingFiles.join(", ")}`);
        }
    }, [selectedRows, memoizedData, fileType, base_url]);

    // Theme-based styling functions - memoized
    const getBackgroundClass = useMemo(() => {
        return theme === 'dark'
            ? 'bg-gray-900 text-white mt-4'
            : 'bg-gradient-to-br from-slate-50 to-blue-50 text-gray-800 mt-4';
    }, [theme]);

    const getTableHeaderClass = useMemo(() => {
        return theme === 'dark'
            ? 'bg-gradient-to-r from-gray-800 to-gray-700 text-white border-b border-gray-600'
            : 'bg-gradient-to-r from-cyan-800 to-cyan-900 text-white border-b border-blue-500';
    }, [theme]);

    const getTableRowClass = useCallback((idx) => {
        if (theme === 'dark') {
            return idx % 2 === 0 ? 'bg-gray-800 hover:bg-gray-750 text-white' : 'bg-gray-700 hover:bg-gray-650 text-white';
        } else {
            return idx % 2 === 0 ? 'bg-white hover:bg-blue-50 text-gray-800' : 'bg-gray-50 hover:bg-blue-50 text-gray-800';
        }
    }, [theme]);

    const getInputClass = useMemo(() => {
        return theme === 'dark'
            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            : 'bg-white border-gray-300 text-gray-800 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
    }, [theme]);

    const getSelectClass = useMemo(() => {
        return theme === 'dark'
            ? 'bg-gray-700 border-gray-600 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            : 'bg-white border-gray-300 text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
    }, [theme]);

    const getButtonClass = useCallback((isActive = false) => {
        if (theme === 'dark') {
            return `px-3 py-2 rounded-lg font-medium transition-all duration-200 ${isActive
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                }`;
        } else {
            return `px-3 py-2 rounded-lg font-medium transition-all duration-200 ${isActive
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`;
        }
    }, [theme]);

    const getDisabledButtonClass = useMemo(() => {
        return theme === 'dark'
            ? 'px-3 py-2 rounded-lg bg-gray-800 text-gray-500 border border-gray-700 cursor-not-allowed'
            : 'px-3 py-2 rounded-lg bg-gray-100 text-gray-400 border border-gray-300 cursor-not-allowed';
    }, [theme]);

    const getNoDataClass = useMemo(() => {
        return theme === 'dark'
            ? 'bg-gray-800 text-gray-300 border border-gray-700'
            : 'bg-white text-gray-600 border border-gray-200';
    }, [theme]);

    const handleFileTypeChange = useCallback((e) => {
        setFileType(e.target.value);
    }, []);

    // Handle popup close
    const handlePopupClose = useCallback(() => {
        setShowRedesignPopup(false);
        setRedesignMessage("");
    }, []);

    return (
        <>
            <Loder status={status} />
            <Chatbox orderid={orderid} />

            {/* ✅ Use the separate popup component */}
            <RedesignPopup
                theme={theme}
                showRedesignPopup={showRedesignPopup}
                pendingRedesignOrders={pendingRedesignOrders}
                redesignMessage={redesignMessage}
                setRedesignMessage={setRedesignMessage}
                isSubmitting={isSubmitting}
                handleRedesignSubmit={handleRedesignSubmit}
                setShowRedesignPopup={handlePopupClose}
            />

            {status === "hide" && (
                <section className={`p-4 rounded-xl ${getBackgroundClass}`}>
                    {(!Array.isArray(columns) || columns.length === 0) && (
                        <div className={`p-8 text-center rounded-xl border ${getNoDataClass}`}>
                            <FontAwesomeIcon icon={faFolderOpen} size="2x" className="mb-3 text-blue-500 opacity-60" />
                            <p className="text-lg font-medium">⚠️ No columns provided.</p>
                        </div>
                    )}

                    {Array.isArray(columns) && columns.length > 0 && (
                        <>
                            {/* Header Controls - ALWAYS SHOW */}
                            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
                                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                                    {/* Rows per page */}
                                    <div className="flex items-center gap-2">
                                        <label className={`text-sm font-medium ${theme === "dark" ? "text-gray-300" : "text-gray-700"}`}>
                                            Rows per page:
                                        </label>
                                        <select
                                            value={rowsPerPage}
                                            onChange={handleRowsPerPageChange}
                                            className={`px-3 py-2 rounded-lg border text-sm focus:outline-none transition-all ${getSelectClass}`}
                                        >
                                            {rowsPerPageOptions.map((option) => (
                                                <option key={option} value={option}>
                                                    {option}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Bulk Actions Toolbar */}
                                    <div className={`flex items-center gap-3 px-4 py-2`}>
                                        <select
                                            value={fileType}
                                            onChange={handleFileTypeChange}
                                            className={`px-3 py-2 rounded-lg border text-sm focus:outline-none transition-all ${getSelectClass}`}
                                        >
                                            {/* <option value="initial">Initial Files</option> */}
                                            <option value="stl">STL Files</option>
                                            <option value="finish">Finished Files</option>
                                        </select>

                                        <button
                                            onClick={handleBulkDownload}
                                            className="px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg shadow-lg flex items-center gap-2 transition-all duration-200 font-medium text-sm cursor-pointer"
                                        >
                                            <FontAwesomeIcon icon={faDownload} /> Download All
                                        </button>

                                        <button
                                            onClick={openRedesignPopup}
                                            className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg shadow-lg flex items-center gap-2 transition-all duration-200 font-medium text-sm cursor-pointer"
                                        >
                                            <FontAwesomeIcon icon={faRepeat} /> Send for Redesign
                                        </button>

                                    </div>
                                </div>

                                {/* Search */}
                                <div className="relative">
                                    <FontAwesomeIcon
                                        icon={faSearch}
                                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Search across all columns..."
                                        value={search}
                                        onChange={handleSearch}
                                        className={`pl-10 pr-4 py-2 w-80 rounded-lg border text-sm focus:outline-none transition-all ${getInputClass}`}
                                    />
                                </div>
                            </div>

                            {/* Table Container */}
                            <div className="rounded-sm overflow-hidden shadow-lg">
                                <div className="overflow-x-auto">
                                    <table className="w-full border-collapse">
                                        <thead>
                                            <tr className={getTableHeaderClass}>
                                                {/* Checkbox Column */}
                                                <th className="p-4 text-center border-r border-gray-500/30 w-16">
                                                    <input
                                                        type="checkbox"
                                                        checked={paginatedData.length > 0 && paginatedData.every((r) => selectedRows.includes(r.orderid))}
                                                        onChange={toggleSelectAll}
                                                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer transform scale-125"
                                                    />
                                                </th>

                                                {columns.map((col) => (
                                                    <th
                                                        key={col.accessor}
                                                        onClick={() => handleSort(col.accessor)}
                                                        className="p-4 text-left font-semibold text-sm cursor-pointer hover:bg-gray-600/50 transition-colors border-r border-gray-500/100 last:border-r-0"
                                                    >
                                                        <div className="flex items-center gap-2 font-bold">
                                                            {col.header}
                                                            <FontAwesomeIcon
                                                                icon={
                                                                    sortConfig.key === col.accessor
                                                                        ? sortConfig.direction === "asc"
                                                                            ? faSortUp
                                                                            : faSortDown
                                                                        : faSort
                                                                }
                                                                className={`text-xs ${sortConfig.key === col.accessor
                                                                    ? 'text-blue-300'
                                                                    : 'text-gray-400'
                                                                    }`}
                                                            />
                                                        </div>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {/* SHOW ERROR OR NO DATA IN TABLE BODY */}
                                            {error || paginatedData.length === 0 ? (
                                                <tr>
                                                    <td colSpan={columns.length + 1} className="p-8 text-center">
                                                        <div className={`flex flex-col items-center justify-center p-8 rounded-lg ${getNoDataClass}`}>
                                                            <FontAwesomeIcon icon={faFolderOpen} size="3x" className="mb-4 text-blue-500 opacity-60" />
                                                            <p className="text-lg font-medium mb-2">
                                                                {error ? "No Data found" : "No records found"}
                                                            </p>
                                                            <p className="text-sm opacity-75">
                                                                {error || (data.length === 0 ? "No data available" : "Try adjusting your search criteria")}
                                                            </p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                paginatedData.map((row, idx) => (
                                                    <tr key={idx} className={`${getTableRowClass(idx)} transition-colors duration-200`}>
                                                        {/* Checkbox Cell */}
                                                        <td className="p-4 text-center border-r border-gray-300/30 dark:border-gray-600/30">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedRows.includes(row.orderid)}
                                                                onChange={() => toggleSelectRow(row.orderid)}
                                                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer transform scale-125"
                                                            />
                                                        </td>

                                                        {columns.map((col) => (
                                                            <td
                                                                key={col.accessor}
                                                                className="p-4 text-[12px] border-r border-gray-300/30 dark:border-gray-600/30 last:border-r-0"
                                                            >
                                                                {
                                                                    col.header === 'Order Id' ? (
                                                                        <div>
                                                                            <Link to={`/user/orderDeatails/${row.orderid}`} className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-bold" > {row.orderid} </Link>
                                                                        </div>
                                                                    ) : col.header === 'Message' ? (
                                                                        <div className="flex justify-center items-center relative">
                                                                            <div className="relative group">
                                                                                <img
                                                                                    src="/img/messages.png"
                                                                                    alt="Message"
                                                                                    className="w-9 h-9 cursor-pointer transition-all duration-200 group-hover:scale-110 group-hover:rotate-12"
                                                                                    onClick={() => openPopup(`${row.orderid}`)}
                                                                                />
                                                                                <span className=" absolute -top-2 -right-2  bg-gradient-to-br from-red-500 via-red-600 to-red-700  text-white text-[12px] font-semibold  rounded-full min-w-[18px] h-[18px]  flex items-center justify-center  shadow-[0_0_8px_rgba(255,0,0,0.6)]ring-2 ring-white/60 backdrop-blur-sm">
                                                                                    {row.totalMessages > 99 ? '99+' : row.totalMessages}
                                                                                </span>

                                                                            </div>
                                                                        </div>
                                                                    ) : col.header === 'Status' ? (
                                                                        <div className="flex justify-center">
                                                                            {(() => {
                                                                                let statusColor = '';
                                                                                let textColor = 'text-white';

                                                                                switch (row.status?.toLowerCase()) {
                                                                                    case 'completed':
                                                                                        statusColor = 'bg-gradient-to-r from-green-500 to-green-600';
                                                                                        break;
                                                                                    case 'pending':
                                                                                        statusColor = 'bg-gradient-to-r from-yellow-500 to-yellow-600';
                                                                                        textColor = 'text-gray-900';
                                                                                        break;
                                                                                    case 'new':
                                                                                        statusColor = 'bg-gradient-to-r from-blue-500 to-blue-600';
                                                                                        break;
                                                                                    case 'cancelled':
                                                                                        statusColor = 'bg-gradient-to-r from-red-500 to-red-600';
                                                                                        break;
                                                                                    case 'qc':
                                                                                        statusColor = 'bg-gradient-to-r from-purple-500 to-purple-600';
                                                                                        break;
                                                                                    case 'redesign':
                                                                                        statusColor = 'bg-gradient-to-r from-orange-500 to-orange-600';
                                                                                        break;
                                                                                    default:
                                                                                        statusColor = 'bg-gradient-to-r from-gray-500 to-gray-600';
                                                                                        break;
                                                                                }

                                                                                return (
                                                                                    <span
                                                                                        className={`px-3 py-1.5 text-xs font-bold rounded-full shadow-md ${statusColor} ${textColor} transition-all duration-200 hover:shadow-lg`}
                                                                                    >
                                                                                        {row.status ? row.status : 'Unknown'}
                                                                                    </span>
                                                                                );
                                                                            })()}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="truncate max-w-xs" title={row[col.accessor] ?? "-"}>
                                                                            {row[col.accessor] ?? "-"}
                                                                        </div>
                                                                    )}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Pagination - Only show when we have data and no error */}
                            {!error && paginatedData.length > 0 && (
                                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
                                    <div className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                        Showing <span className="font-bold">{paginatedData.length}</span> of <span className="font-bold">{filteredData.length}</span> entries
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className={currentPage === 1 ? getDisabledButtonClass : getButtonClass()}
                                        >
                                            Previous
                                        </button>

                                        {getPageNumbers(totalPages, currentPage).map((page, i) => (
                                            <button
                                                key={i}
                                                className={
                                                    typeof page === "number" && currentPage === page
                                                        ? getButtonClass(true)
                                                        : page === "..."
                                                            ? getDisabledButtonClass
                                                            : getButtonClass()
                                                }
                                                onClick={() => typeof page === "number" && handlePageChange(page)}
                                                disabled={page === "..."}
                                            >
                                                {page}
                                            </button>
                                        ))}

                                        <button
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className={currentPage === totalPages ? getDisabledButtonClass : getButtonClass()}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </section>
            )}
        </>
    );
}